import { useRef, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";

const BASE = "/assets/";

const industries = [
  { name: "Corporate Organizations", img: BASE + "a501d21b8db78aa9e3f0d8029f655bf98518cacdabc5af3f0ee3aec279bbe926.jpeg" },
  { name: "Finance and Technology", img: BASE + "223c990e9f8d6db1e05de6fb7f58f1f7e64f46cd85f553fcc20026063841f3e4.jpeg" },
  { name: "Hospitality (Hotels & Restaurants)", img: BASE + "c5902dd765d5a187f918042fea4d57d7c67fe2e474370521eb803f2d79b9fc08.jpeg" },
  { name: "Manufacturing & Construction", img: BASE + "06bb3f6c798f08de6d27d5813e24b154d3e3dbf430664b9be27f1a4e170a8c03.jpeg" },
  { name: "Logistics", img: BASE + "1df69b03153bd59260beb8dd5e4b2b9bd99f09f1c07fee98f6888e2c71fc7b17.jpeg" },
  { name: "Aviation", img: BASE + "bab05a3f24f0c6ba48870f9f5e7a4b84aa4d12c48a6dcd59f90a44b678725f6f.jpeg" },
  { name: "Maritime & Shipping", img: BASE + "03a4d4c8d4e4404078c464814618f81901464804390cc87fbb15ee7359701b7c.jpeg" },
  { name: "Oil and Gas", img: BASE + "5936835ff96ebc38877e608e869dc7bb5bbb0eb7ebd1e0656d52effee1bb54ce.jpeg" },
];

// duplicated for a seamless infinite loop
const loopItems = [...industries, ...industries];
const GAP = 24;

export default function IndustriesJourney() {
  const scrollRef = useRef(null);
  const pausedRef = useRef(false);
  const posRef = useRef(0);

  // continuous auto-scroll (accumulate in a float, never read scrollLeft back)
  useEffect(() => {
    let raf;
    let last = performance.now();
    const speed = 70; // px per second
    const tick = (now) => {
      const el = scrollRef.current;
      const dt = Math.min((now - last) / 1000, 0.05);
      last = now;
      if (el && !pausedRef.current && el.scrollWidth > el.clientWidth) {
        const half = el.scrollWidth / 2;
        posRef.current += speed * dt;
        if (posRef.current >= half) posRef.current -= half;
        el.scrollLeft = posRef.current;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const cardWidth = () => {
    const el = scrollRef.current;
    const card = el?.querySelector("[data-industry-card]");
    return (card?.offsetWidth || 300) + GAP;
  };

  // move exactly one image per click
  const nudge = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    const half = el.scrollWidth / 2;
    const w = cardWidth();
    pausedRef.current = true;
    let target = el.scrollLeft + dir * w;
    if (target < 0) { el.scrollLeft += half; target += half; }
    else if (target >= half) { el.scrollLeft -= half; target -= half; }
    el.scrollTo({ left: target, behavior: "smooth" });
    posRef.current = target;
    window.clearTimeout(el._resume);
    el._resume = window.setTimeout(() => { pausedRef.current = false; }, 900);
  };

  // drag to scroll
  const drag = useRef({ down: false, startX: 0, startLeft: 0 });
  const onDown = (e) => {
    const el = scrollRef.current;
    pausedRef.current = true;
    drag.current = { down: true, startX: e.pageX, startLeft: el.scrollLeft };
  };
  const onMove = (e) => {
    if (!drag.current.down) return;
    e.preventDefault();
    const el = scrollRef.current;
    el.scrollLeft = drag.current.startLeft - (e.pageX - drag.current.startX);
  };
  const onUp = () => {
    const el = scrollRef.current;
    if (drag.current.down && el) posRef.current = el.scrollLeft;
    drag.current.down = false;
    pausedRef.current = false;
  };

  // touch swipe support for mobile
  const touch = useRef({ startX: 0, startLeft: 0 });
  const onTouchStart = (e) => {
    const el = scrollRef.current;
    if (!el) return;
    pausedRef.current = true;
    touch.current = { startX: e.touches[0].pageX, startLeft: el.scrollLeft };
  };
  const onTouchMove = (e) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollLeft = touch.current.startLeft - (e.touches[0].pageX - touch.current.startX);
  };
  const onTouchEnd = () => {
    const el = scrollRef.current;
    if (el) posRef.current = el.scrollLeft;
    pausedRef.current = false;
  };

  return (
    <section data-testid="industries-section" className="py-16 sm:py-20 md:py-32 overflow-hidden">
      <Reveal className="max-w-7xl mx-auto px-6 md:px-12">
        <h2 className="font-display text-[#12294d] text-3xl sm:text-4xl md:text-6xl tracking-tight" data-testid="industries-heading">
          04 — Industries We <span className="text-[#2ab5cf]">Serve</span>
        </h2>
      </Reveal>

      <div className="relative mt-12">
        <button
          onClick={() => nudge(-1)}
          aria-label="Previous industry"
          data-testid="industries-prev"
          className="absolute left-2 sm:left-3 md:left-6 top-[94px] sm:top-[104px] md:top-[120px] -translate-y-1/2 z-20 h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-white/95 backdrop-blur border border-[#1a3a6b]/15 shadow-lg shadow-[#1a3a6b]/15 flex items-center justify-center text-[#1a3a6b] hover:bg-[#1a3a6b] hover:text-white transition-colors"
        >
          <ChevronLeft size={20} />
        </button>
        <button
          onClick={() => nudge(1)}
          aria-label="Next industry"
          data-testid="industries-next"
          className="absolute right-2 sm:right-3 md:right-6 top-[94px] sm:top-[104px] md:top-[120px] -translate-y-1/2 z-20 h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-white/95 backdrop-blur border border-[#1a3a6b]/15 shadow-lg shadow-[#1a3a6b]/15 flex items-center justify-center text-[#1a3a6b] hover:bg-[#1a3a6b] hover:text-white transition-colors"
        >
          <ChevronRight size={20} />
        </button>

        <div
          ref={scrollRef}
          onMouseDown={onDown}
          onMouseMove={onMove}
          onMouseUp={onUp}
          onMouseLeave={onUp}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          className="flex gap-4 sm:gap-6 overflow-x-auto px-5 sm:px-6 md:px-12 pb-4 cursor-grab active:cursor-grabbing select-none touch-pan-y [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {loopItems.map((ind, i) => (
            <div
              key={i}
              data-industry-card
              className="shrink-0 w-[82vw] max-w-[280px] md:w-[340px] rounded-[1.75rem] overflow-hidden bg-white border border-[#1a3a6b]/10 shadow-lg shadow-[#1a3a6b]/5"
              data-testid={i < industries.length ? `industry-card-${i}` : undefined}
            >
              <img
                src={ind.img}
                alt={ind.name}
                draggable={false}
                className="w-full h-52 md:h-60 object-cover pointer-events-none"
                loading="eager"
              />
              <p className="px-6 py-5 font-display font-bold text-lg md:text-xl text-[#12294d] tracking-tight">
                {ind.name}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
